import {
  createBrowserRouter,
  Link,
  RouterProvider,
  type RouteObject,
} from "react-router-dom";
import { AccountsPage } from "../features/accounts/AccountsPage.js";
import { ForcePasswordChangePage } from "../features/auth/ForcePasswordChangePage.js";
import { LoginPage } from "../features/auth/LoginPage.js";
import { NewOrderPage } from "../features/cases/order-form/NewOrderPage.js";
import { CATALOG_PATH, STOCK_ORDER_REPORT_PATH } from "../features/cases/presentation.js";
import {
  OrderReceiptPage,
  RECEIPT_ALIAS_ROUTE_PATH,
  RECEIPT_ROUTE_PATH,
} from "../features/cases/receipt/OrderReceiptPage.js";
import {
  WHOLESALE_TICKET_ALIAS_ROUTE_PATH,
  WHOLESALE_TICKET_ROUTE_PATH,
  WholesaleTicketPage,
} from "../features/wholesale/ticket/WholesaleTicketPage.js";
import { StockOrderReportPage } from "../features/cases/reports/StockOrderReportPage.js";
import { CatalogPage } from "../features/catalog/CatalogPage.js";
import { PartsWorkspacePage } from "../features/cases/workspace/PartsWorkspacePage.js";
import { SharedWorkspacePage } from "../features/cases/workspace/SharedWorkspacePage.js";
import { CustomersPage } from "../features/wholesale/CustomersPage.js";
import { NewWholesaleOrderPage } from "../features/wholesale/NewWholesaleOrderPage.js";
import {
  CUSTOMERS_PATH,
  NEW_WHOLESALE_ORDER_PATH,
  WHOLESALE_CATALOG_PATH,
  WHOLESALE_PATH,
  WHOLESALE_STOCK_ORDER_REPORT_PATH,
} from "../features/wholesale/presentation.js";
import { WholesaleWorkspacePage } from "../features/wholesale/WholesaleWorkspacePage.js";
import { buttonAttributes } from "../ui/Button.js";
import {
  CHANGE_PASSWORD_PATH,
  LOGIN_PATH,
  RequireSession,
} from "./RequireSession.js";
import { RoleHomeRedirect } from "./RoleHomeRedirect.js";

/**
 * The route table.
 *
 * Exported as data so tests drive it through a memory router and the
 * production entry point mounts it in a browser router. Every route except
 * `/login` sits behind {@link RequireSession}. `/parts` is the Parts desktop
 * workspace, `/parts/new` its order form and `/parts/reports/stock-orders`
 * its stock order report; `/wholesale`, `/wholesale/new` and
 * `/wholesale/customers` are the wholesale side of the same Parts login
 * (wholesale spec §4), and `/wholesale/catalog` and
 * `/wholesale/reports/stock-orders` are the two shared pages under that
 * side's address (the same pages, framed by the wholesale shell);
 * `/workspace` is the shared Surface workspace for the non-Parts roles;
 * `/cases/:caseId/print` is the printable order receipt.
 */

function NotFoundPage() {
  return (
    <main className="placeholder-page">
      <section className="placeholder-card">
        <h1 className="placeholder-card__title">Page not found</h1>
        <p className="placeholder-card__lead">
          That address doesn't match anything here.
        </p>
        <Link to="/" {...buttonAttributes({ variant: "primary" })}>
          Go to my home
        </Link>
      </section>
    </main>
  );
}

export const appRoutes: RouteObject[] = [
  { path: LOGIN_PATH, element: <LoginPage /> },
  {
    path: CHANGE_PASSWORD_PATH,
    element: (
      <RequireSession allowTemporaryPassword>
        <ForcePasswordChangePage />
      </RequireSession>
    ),
  },
  {
    element: <RequireSession />,
    children: [
      { path: "/", element: <RoleHomeRedirect /> },
      { path: "/parts", element: <PartsWorkspacePage /> },
      // Manual order creation (Task 11); Parts only — the page sends other roles home.
      { path: "/parts/new", element: <NewOrderPage /> },
      // The stock order report (owner request 2026-09-17); Parts only, the same gate.
      { path: STOCK_ORDER_REPORT_PATH, element: <StockOrderReportPage /> },
      { path: CATALOG_PATH, element: <CatalogPage /> },
      // The wholesale side (wholesale spec §4); Parts only, the same gate in each shell.
      { path: WHOLESALE_PATH, element: <WholesaleWorkspacePage /> },
      { path: NEW_WHOLESALE_ORDER_PATH, element: <NewWholesaleOrderPage /> },
      { path: CUSTOMERS_PATH, element: <CustomersPage /> },
      // The shared pages under the wholesale address: the page reads its side from the path.
      { path: WHOLESALE_STOCK_ORDER_REPORT_PATH, element: <StockOrderReportPage /> },
      { path: WHOLESALE_CATALOG_PATH, element: <CatalogPage /> },
      { path: "/workspace/*", element: <SharedWorkspacePage /> },
      // The printable order receipt (Parts; the server refuses other roles).
      { path: RECEIPT_ROUTE_PATH, element: <OrderReceiptPage /> },
      { path: RECEIPT_ALIAS_ROUTE_PATH, element: <OrderReceiptPage /> },
      // The printable wholesale order ticket (Parts; the server refuses other roles).
      { path: WHOLESALE_TICKET_ROUTE_PATH, element: <WholesaleTicketPage /> },
      { path: WHOLESALE_TICKET_ALIAS_ROUTE_PATH, element: <WholesaleTicketPage /> },
      // Administration is a capability, not a role: the page itself turns a
      // non-admin session away, and the server refuses the routes regardless.
      { path: "/accounts", element: <AccountsPage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];

let browserRouter: ReturnType<typeof createBrowserRouter> | null = null;

/** Mounts the route table in the browser's history; created on first render, not at import. */
export function AppRouter() {
  browserRouter ??= createBrowserRouter(appRoutes);
  return <RouterProvider router={browserRouter} />;
}
